import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";

// Stores
import { useStores } from "../../../use-stores";

// Componenets
import ImageUpload from "../../../../../views/components/common/upload/ImageUpload";
import { useNotification } from "../../../../../utils/NotificationsContext";

// Models
import {
  ProductVariant,
  ProductVariantImages,
} from "../../../../../models/Products/ProductVariant";
import ImageAPI from "../../../api/ImageAPI";

type ProductVariantImageListProp = {
  variant: ProductVariant;
};

const ProductVariantImageList = ({ variant }: ProductVariantImageListProp) => {
  const { productStore } = useStores();
  const { showError, showSuccess } = useNotification();

  const [selectedImages, setSelectedImages] = useState<File[]>([]);

  const saveVariantImages = (variantId: number) => {
    if (selectedImages.length > 0) {
      const imageAPI = new ImageAPI();
      imageAPI
        .uploadImage(selectedImages, "product-images")
        .then((uploadedUrls) => {
          const newImageData: ProductVariantImages[] = uploadedUrls.map(
            (url) => {
              return { image_url: url } as ProductVariantImages;
            },
          );
          productStore.addVariantImages(variantId, newImageData);
          productStore
            .updateVariantImage()
            .then(() => {
              setSelectedImages([]);
              showSuccess("Product updated successfully");
            })
            .catch((err) => {
              showError(
                "Error occurred",
                err?.response?.data?.message ||
                  "Something went wrong! Please try again later.",
              );
            });
        })
        .catch((err) => {
          showError(
            "Error occurred",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          );
        });
    } else
      productStore
        .updateVariantImage()
        .then(() => {
          showSuccess("Product updated successfully");
        })
        .catch((err) => {
          showError(
            "Error occurred",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          );
        });
  };

  const getTitle = (variant: ProductVariant): string => {
    let title = String(variant.id) + ".";

    if (variant.options_name && variant.options_name.length !== 0)
      title = title + " " + variant.options_name;

    if (variant.title && variant.title.length !== 0)
      title = title === "" ? variant.title : title + " - " + variant.title;

    if (variant.value && variant.value.length !== 0)
      title = title === "" ? variant.value : title + " - " + variant.value;

    if (variant.sku && variant.sku.length !== 0)
      title = title + " - " + variant.sku;

    return title;
  };

  return (
    <Box sx={{ margin: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
          {getTitle(variant)}
        </Typography>
        <Box>
          <Button
            variant="outlined"
            color="secondary"
            sx={{ ml: 2, maxHeight: "38px", padding: "6px 16px" }}
            onClick={() => {
              setSelectedImages([]);
              productStore.discardVariantImagesChanges(variant.id!);
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ ml: 2, maxHeight: "38px", padding: "6px 16px" }}
            onClick={() => saveVariantImages(variant.id!)}
          >
            Save
          </Button>
        </Box>
      </Box>
      <ImageUpload
        showImagesList={true}
        id={`${variant.id!}`}
        size="medium"
        existingImageList={
          variant.image_urls?.map((image) => ({
            id: image.id,
            title: "Product Image",
            img: image.image_url,
          })) || []
        }
        newImageList={selectedImages}
        deleteImage={(image) => {
          productStore.removeVariantImage(
            { id: image.id, url: image.img },
            variant.id!,
          );
        }}
        removeImage={(image) => {
          const files = selectedImages.filter(
            (file) => file.name !== image.title,
          );
          setSelectedImages(files);
        }}
        addImagesToBeUploaded={(files) => {
          setSelectedImages((prev) => [...prev, ...files]);
        }}
      />
    </Box>
  );
};

export default ProductVariantImageList;
